import { API_URL } from "env";

interface LoginResponse {
    status: boolean;
    msg: string;
    data: {
        fullname: string;
        code: string;
        email: string;
        info: {
            id: number;
            parent_id: number;
            user_id_at: string | null;
            first_name: string;
            last_name: string;
            fullname: string;
            code: string;
            username: string | null;
            email: string;
            avatar: string;
            phone: string;
            address: string;
            is_active: number;
            created_at: string;
            updated_at: string;
            [key: string]: any;
        };
        access_token: string;
        token_type: string;
    } | null;
}

interface SignupResponse {
    status: boolean;
    msg: string;
    data: {
        fullname: string;
        first_name: string;
        last_name: string;
        phone: string;
        code: string;
        email: string;
        access_token: string;
        token_type: string;
    } | null;
}


/**
 * Authenticates a customer by sending their credentials to the login API endpoint.
 *
 * @param email - The customer's email address.
 * @param password - The customer's password.
 * @returns A promise that resolves to a {@link LoginResponse} indicating the success or failure of the authentication attempt.
 *
 * @throws Will not throw an error; instead, it catches exceptions and returns a failure response object.
 */
async function CustomerLoginEmail(email: string, password: string): Promise<LoginResponse> {
    try {
        const data = new FormData();
        data.append('account', email);
        data.append('password', password);

        const response = await fetch(`${API_URL}/api/v1/fe/customer/login`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
            },
            body: data,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        localStorage.setItem('token', responseData.data?.access_token || '');
        localStorage.setItem('user', JSON.stringify(responseData.data?.info || {}));
        return responseData as LoginResponse;
    } catch (error) {
        console.error('Failed to login:', error);
        return {
            status: false,
            msg: 'Login failed',
            data: null,
        };
    }
}

async function CustomerLoginPhone(phone: string, password: string): Promise<LoginResponse> {
    try {
        const data = new FormData();
        data.append('account', phone);
        data.append('password', password);

        const response = await fetch(`${API_URL}/api/v1/fe/customer/login`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
            },
            body: data,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        localStorage.setItem('token', responseData.data?.access_token || '');
        localStorage.setItem('user', JSON.stringify(responseData.data?.info || {}));
        return responseData as LoginResponse;
    } catch (error) {
        console.error('Failed to login:', error);
        return {
            status: false,
            msg: 'Login failed',
            data: null,
        };
    }
}

/**
 * Registers a new customer account by sending their personal information to the API.
 * 
 * @param firstname - The first name of the customer.
 * @param lastname - The last name of the customer.
 * @param phone - The phone number of the customer.
 * @param email - The email address for the account.
 * @param password - The account password.
 * @returns A promise that resolves to a {@link SignupResponse} containing the registration status and data.
 * 
 * @throws Will log an error to the console and return a failure state if the network request fails 
 * or the response status is not OK.
 */
async function CustomerSignup(
    firstname: string,
    lastname: string,
    phone: string,
    email: string,
    password: string
): Promise<SignupResponse> {
    try {
        const data = new FormData();
        data.append('first_name', firstname);
        data.append('last_name', lastname);
        data.append('fullname', `${firstname} ${lastname}`);
        data.append('phone', phone);
        data.append('email', email);
        data.append('password', password);

        const response = await fetch(`${API_URL}/api/v1/fe/customer/create`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
            },
            body: data,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        return responseData as SignupResponse;
    } catch (error) {
        console.error('Failed to signup:', error);
        return {
            status: false,
            msg: 'Signup failed',
            data: null,
        };
    }
}

export {
    CustomerLoginEmail,
    CustomerLoginPhone,
    CustomerSignup,
    CustomerForgotPassword,
    CustomerResetPassword,
}

interface ForgotPasswordResponse {
    status: boolean;
    msg?: string;
    error?: Record<string, string>;
}

async function CustomerForgotPassword(email: string, link_logo: string): Promise<ForgotPasswordResponse> {
    try {
        const data = new FormData();
        data.append('email', email);
        data.append('domain_name', window.location.origin);
        data.append('link_logo', link_logo);
        data.append('path_name', `${window.location.origin}/reset-password`);
        data.append('name_web', document.title || 'Dược Tâm Anh');

        const response = await fetch(`${API_URL}/api/v1/fe/customer/forgot_password`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
            },
            body: data,
        });

        const responseData = await response.json();
        if (!responseData.status && responseData.error) {
            const errorMessages = Object.values(responseData.error).join(', ');
            return { status: false, msg: errorMessages, error: responseData.error };
        }

        return responseData as ForgotPasswordResponse;
    } catch (error) {
        console.error('Failed to request forgot password:', error);
        return { status: false, msg: 'Gửi yêu cầu thất bại' };
    }
}

interface ResetPasswordResponse {
    status: boolean;
    msg?: string;
    error?: Record<string, string>;
}

async function CustomerResetPassword(token: string, newPassword: string, confirmPassword: string): Promise<ResetPasswordResponse> {
    try {
        const data = new FormData();
        data.append('token_password', token);
        data.append('new_password', newPassword);
        data.append('confirm_password', confirmPassword);

        const response = await fetch(`${API_URL}/api/v1/fe/customer/token_update_password`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
            },
            body: data,
        });

        const responseData = await response.json();
        if (!responseData.status && responseData.error) {
            const errorMessages = Object.values(responseData.error).join(', ');
            return { status: false, msg: errorMessages, error: responseData.error };
        }

        return responseData as ResetPasswordResponse;
    } catch (error) {
        console.error('Failed to reset password:', error);
        return { status: false, msg: 'Đổi mật khẩu thất bại' };
    }
}